import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';

export default class BackOfANapkinMetricsChartModalComponent extends Component {
  @service('byod-metrics') byodMetrics;
  @service store;

  @tracked level = 'month';
  @tracked aggregates = [];
  @tracked isLoading = false;
  @tracked error = null;
  @tracked dateFrom = '';
  @tracked dateTo = '';
  @tracked measurements = [];
  @tracked measurementsLoading = false;
  @tracked measurementsExpanded = false;
  @tracked addMeasurementOpen = false;
  @tracked newMeasurementDate = '';
  @tracked newMeasurementValue = '';
  @tracked newCommentDelta = { ops: [] };
  @tracked newMeasurementIsIncrement = false;
  @tracked isSaving = false;

  get today() {
    return new Date().toISOString().split('T')[0];
  }

  get hasData() {
    return !this.isLoading && !this.error && this.aggregates.length > 0;
  }

  @action
  async loadAggregates() {
    this.isLoading = true;
    this.error = null;
    const from = this.dateFrom || null;
    const to = this.dateTo || null;
    try {
      this.aggregates = await this.byodMetrics.fetchAggregates(
        this.args.metric.id,
        this.level,
        null,
        from,
        to,
      );
    } catch (e) {
      this.error = e.message;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async loadMeasurements() {
    this.measurementsLoading = true;
    try {
      const result = await this.store.query('measurement', {
        filter: { metric: this.args.metric.id },
      });
      this.measurements = [...result.slice()].sort((a, b) => {
        return new Date(b.measured_at_utc) - new Date(a.measured_at_utc);
      });
    } finally {
      this.measurementsLoading = false;
    }
  }

  @action
  setLevel(level) {
    this.level = level;
    if (level === 'day' && !this.dateFrom && !this.dateTo) {
      const now = new Date();
      const from = new Date(now);
      from.setDate(now.getDate() - 90);
      this.dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      this.dateFrom = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}`;
    }
    this.loadAggregates();
  }

  @action
  setDateFrom(event) {
    this.dateFrom = event.target.value;
  }

  @action
  setDateTo(event) {
    this.dateTo = event.target.value;
  }

  @action
  applyDateRange() {
    this.loadAggregates();
  }

  @action
  toggleMeasurements() {
    this.measurementsExpanded = !this.measurementsExpanded;
    if (this.measurementsExpanded) {
      this.loadMeasurements();
    }
  }

  @action
  openAddForm() {
    const today = new Date();
    this.newMeasurementDate = today.toISOString().split('T')[0];
    this.newMeasurementValue = '';
    this.newCommentDelta = { ops: [] };
    this.newMeasurementIsIncrement = false;
    this.addMeasurementOpen = true;
    this.measurementsExpanded = true;
  }

  @action
  closeAddForm() {
    this.addMeasurementOpen = false;
  }

  @action
  setNewDate(event) {
    this.newMeasurementDate = event.target.value;
  }

  @action
  setNewValue(event) {
    this.newMeasurementValue = event.target.value;
  }

  @action
  setNewComment(delta) {
    this.newCommentDelta = delta;
  }

  @action
  toggleIsIncrement() {
    this.newMeasurementIsIncrement = !this.newMeasurementIsIncrement;
  }

  @action
  async saveMeasurement() {
    if (!this.newMeasurementValue || !this.newMeasurementDate) return;
    this.isSaving = true;
    try {
      const metricModel = this.args.metric?.content ?? this.args.metric;
      const measurement = this.store.createRecord('measurement', {
        metric: metricModel,
        value: this.newMeasurementValue,
        measured_at_utc: this.newMeasurementDate,
        comment: this.newCommentDelta,
        increment: this.newMeasurementIsIncrement,
        decimals: metricModel.decimals ?? 2,
      });
      await measurement.save();
      this.addMeasurementOpen = false;
      await this.loadMeasurements();
      await this.loadAggregates();
    } catch (e) {
      console.error('Failed to save measurement:', e);
    } finally {
      this.isSaving = false;
    }
  }
}
